export const SEQUENCE_JSON_VERSION = 1;
export const DEFAULT_SOUND_VOLUME = 1;
export const DEFAULT_SOUND_PITCH = 1;
export const DEFAULT_DELAY_TICKS = 1;
export const MIN_DELAY_TICKS = 1;
export const MAX_DELAY_TICKS = 50;
export const MIN_SOUND_VOLUME = 0;
export const MAX_SOUND_VOLUME = 1;
export const MIN_SOUND_PITCH = 0.5;
export const MAX_SOUND_PITCH = 2;

export interface SoundInstruction {
  id: string;
  type: 'sound';
  soundEvent: string;
  volume: number;
  pitch: number;
}

export interface DelayInstruction {
  id: string;
  type: 'delay';
  ticks: number;
}

export type SequencerInstruction = SoundInstruction | DelayInstruction;

interface UndoSnapshot {
  instructions: SequencerInstruction[];
  message: string;
}

export interface SequencerState {
  instructions: SequencerInstruction[];
  locked: boolean;
  lastUndo?: UndoSnapshot;
}

export type SequencerAction =
  | { type: 'ADD_SOUND'; id: string; soundEvent: string; index?: number }
  | { type: 'ADD_DELAY'; id: string; ticks?: number; index?: number }
  | { type: 'MOVE_INSTRUCTION'; id: string; index: number }
  | { type: 'UPDATE_SOUND'; id: string; volume?: number; pitch?: number }
  | { type: 'UPDATE_DELAY'; id: string; ticks: number }
  | { type: 'DELETE_INSTRUCTION'; id: string; undoable?: boolean }
  | { type: 'CLEAR_ALL'; undoable?: boolean }
  | { type: 'UNDO_LAST_DELETE' }
  | { type: 'DISMISS_UNDO' }
  | { type: 'IMPORT_SEQUENCE'; instructions: SequencerInstruction[] }
  | { type: 'SET_LOCKED'; locked: boolean };

export type ParseSequenceResult =
  | { ok: true; instructions: SequencerInstruction[] }
  | { ok: false; error: string };

interface ExportedSoundInstruction {
  type: 'sound';
  soundEvent: string;
  volume: number;
  pitch: number;
}

interface ExportedDelayInstruction {
  type: 'delay';
  ticks: number;
}

type ExportedInstruction = ExportedSoundInstruction | ExportedDelayInstruction;

export interface ExportedSequenceV1 {
  version: 1;
  instructions: ExportedInstruction[];
}

export function createInitialSequencerState(): SequencerState {
  return {
    instructions: [],
    locked: false,
  };
}

export function createSoundInstruction(id: string, soundEvent: string): SoundInstruction {
  return {
    id,
    type: 'sound',
    soundEvent,
    volume: DEFAULT_SOUND_VOLUME,
    pitch: DEFAULT_SOUND_PITCH,
  };
}

export function createDelayInstruction(id: string, ticks = DEFAULT_DELAY_TICKS): DelayInstruction {
  return {
    id,
    type: 'delay',
    ticks: clampDelayTicks(ticks),
  };
}

export function sequencerReducer(state: SequencerState, action: SequencerAction): SequencerState {
  if (action.type === 'SET_LOCKED') {
    return { ...state, locked: action.locked };
  }

  if (action.type === 'UNDO_LAST_DELETE') {
    if (!state.lastUndo || state.locked) return state;
    return {
      ...state,
      instructions: state.lastUndo.instructions,
      lastUndo: undefined,
    };
  }

  if (action.type === 'DISMISS_UNDO') {
    return {
      ...state,
      lastUndo: undefined,
    };
  }

  if (state.locked) return state;

  switch (action.type) {
    case 'ADD_SOUND':
      return {
        ...state,
        instructions: insertAt(
          state.instructions,
          createSoundInstruction(action.id, action.soundEvent),
          action.index
        ),
        lastUndo: undefined,
      };

    case 'ADD_DELAY':
      return {
        ...state,
        instructions: insertAt(
          state.instructions,
          createDelayInstruction(action.id, action.ticks),
          action.index
        ),
        lastUndo: undefined,
      };

    case 'MOVE_INSTRUCTION': {
      const fromIndex = state.instructions.findIndex(instruction => instruction.id === action.id);
      if (fromIndex === -1) return state;

      const next = [...state.instructions];
      const [instruction] = next.splice(fromIndex, 1);
      next.splice(clampIndex(action.index, next.length), 0, instruction);
      return {
        ...state,
        instructions: next,
        lastUndo: undefined,
      };
    }

    case 'UPDATE_SOUND':
      return {
        ...state,
        instructions: state.instructions.map(instruction => {
          if (instruction.type !== 'sound' || instruction.id !== action.id) return instruction;
          return {
            ...instruction,
            volume: typeof action.volume === 'number'
              ? clampNumber(action.volume, MIN_SOUND_VOLUME, MAX_SOUND_VOLUME)
              : instruction.volume,
            pitch: typeof action.pitch === 'number'
              ? clampNumber(action.pitch, MIN_SOUND_PITCH, MAX_SOUND_PITCH)
              : instruction.pitch,
          };
        }),
      };

    case 'UPDATE_DELAY':
      return {
        ...state,
        instructions: state.instructions.map(instruction => {
          if (instruction.type !== 'delay' || instruction.id !== action.id) return instruction;
          return { ...instruction, ticks: clampDelayTicks(action.ticks) };
        }),
      };

    case 'DELETE_INSTRUCTION': {
      const next = state.instructions.filter(instruction => instruction.id !== action.id);
      if (next.length === state.instructions.length) return state;
      return {
        ...state,
        instructions: next,
        lastUndo: action.undoable
          ? { instructions: state.instructions, message: 'Instruction removed' }
          : undefined,
      };
    }

    case 'CLEAR_ALL':
      return {
        ...state,
        instructions: [],
        lastUndo: action.undoable && state.instructions.length
          ? { instructions: state.instructions, message: 'Sequence cleared' }
          : undefined,
      };

    case 'IMPORT_SEQUENCE':
      return {
        ...state,
        instructions: action.instructions,
        lastUndo: undefined,
      };

    default:
      return state;
  }
}

export function exportSequenceJson(instructions: SequencerInstruction[]): string {
  const exported: ExportedSequenceV1 = {
    version: SEQUENCE_JSON_VERSION,
    instructions: instructions.map(stripTransientFields),
  };

  return JSON.stringify(exported, null, 2);
}

export function parseSequenceJson(
  rawJson: string,
  knownSoundEvents: Set<string>,
  nextId: () => string
): ParseSequenceResult {
  let parsed: any;

  try {
    parsed = JSON.parse(rawJson);
  } catch {
    return { ok: false, error: 'Invalid JSON.' };
  }

  if (!parsed || parsed.version !== SEQUENCE_JSON_VERSION || !Array.isArray(parsed.instructions)) {
    return { ok: false, error: 'Only sequencer JSON version 1 is supported.' };
  }

  const instructions: SequencerInstruction[] = [];

  for (const rawInstruction of parsed.instructions) {
    if (!rawInstruction || typeof rawInstruction.type !== 'string') {
      return { ok: false, error: 'Every instruction needs a valid type.' };
    }

    if (rawInstruction.type === 'sound') {
      if (
        typeof rawInstruction.soundEvent !== 'string' ||
        !knownSoundEvents.has(rawInstruction.soundEvent)
      ) {
        return { ok: false, error: `Unknown sound event: ${rawInstruction.soundEvent || 'missing'}.` };
      }

      if (
        !isFiniteNumber(rawInstruction.volume) ||
        rawInstruction.volume < MIN_SOUND_VOLUME ||
        rawInstruction.volume > MAX_SOUND_VOLUME ||
        !isFiniteNumber(rawInstruction.pitch) ||
        rawInstruction.pitch < MIN_SOUND_PITCH ||
        rawInstruction.pitch > MAX_SOUND_PITCH
      ) {
        return { ok: false, error: 'Sound volume or pitch is out of range.' };
      }

      instructions.push({
        id: nextId(),
        type: 'sound',
        soundEvent: rawInstruction.soundEvent,
        volume: rawInstruction.volume,
        pitch: rawInstruction.pitch,
      });
    } else if (rawInstruction.type === 'delay') {
      if (
        !Number.isInteger(rawInstruction.ticks) ||
        rawInstruction.ticks < MIN_DELAY_TICKS ||
        rawInstruction.ticks > MAX_DELAY_TICKS
      ) {
        return { ok: false, error: `Delay ticks must be ${MIN_DELAY_TICKS}-${MAX_DELAY_TICKS}.` };
      }

      instructions.push({
        id: nextId(),
        type: 'delay',
        ticks: rawInstruction.ticks,
      });
    } else {
      return { ok: false, error: `Unknown instruction type: ${rawInstruction.type}.` };
    }
  }

  return { ok: true, instructions };
}

function stripTransientFields(instruction: SequencerInstruction): ExportedInstruction {
  if (instruction.type === 'sound') {
    return {
      type: 'sound',
      soundEvent: instruction.soundEvent,
      volume: instruction.volume,
      pitch: instruction.pitch,
    };
  }

  return {
    type: 'delay',
    ticks: instruction.ticks,
  };
}

function insertAt<T>(items: T[], item: T, index?: number): T[] {
  const next = [...items];
  next.splice(clampIndex(index ?? next.length, next.length), 0, item);
  return next;
}

function clampIndex(index: number, length: number): number {
  return Math.max(0, Math.min(index, length));
}

function clampDelayTicks(ticks: number): number {
  return Math.round(clampNumber(ticks, MIN_DELAY_TICKS, MAX_DELAY_TICKS));
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}
